import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { MagnifyingGlassIcon as SearchIcon, XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { Drawer, Search } from '@loomstateorg/shared-frontend';
import HeaderNavigationButtonDesktop from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/header-navigation/header-navigation-desktop/header-navigation-button';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import useScrollDirection from '@loomstateorg/shared-frontend/hooks/useScrollDirection';

export interface Props {
  links: any[]; //TODO FIX THIS TYPE
  categories: any;
  search: boolean;
  navLinkTypographyClassName?: string;
  searchDrawerClassName?: string;
  searchSubmitButtonClassName?: string;
  searchIconFocusedClassName?: string;
  children?: React.ReactNode;
}

const HeaderNavigationDesktop: React.FC<Props> = ({
  links,
  categories,
  search,
  navLinkTypographyClassName,
  searchDrawerClassName,
  searchSubmitButtonClassName,
  searchIconFocusedClassName,
}) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const scrollDirection = useScrollDirection(5, -1);
  const navigationClassNames = useClassNames([
    'hidden items-center lg:flex duration-150 transition-all w-fit',
    scrollDirection === 'down' ? 'h-0 pointer-events-none' : 'h-52 opacity-1 pointer-events-auto',
  ]);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const router = useRouter();

  const onSearchClicked = () => {
    setIsDrawerOpen(true);
  };

  useEffect(() => {
    const handleRouteChange = () => {
      setIsDrawerOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  return (
    <nav aria-label={formatMessage({ id: 'header.nav.primary', defaultMessage: 'Primary' })} data-testid="header-navbar">
      {links && (
        <div className={navigationClassNames}>
          <ul className="flex w-fit justify-start" data-testid="nav-category-list">
            {links.map((link, index) => (
              <li key={index} data-testid={`header-nav-category-${(link.linkName || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
                <HeaderNavigationButtonDesktop
                  link={link.linkUrl}
                  linkName={link.linkName}
                  typographyClassName={navLinkTypographyClassName}
                  index={index}
                />
              </li>
            ))}
            {search && (
              <li className="relative mx-10 hidden h-32 lg:block ">
                <button
                  type="button"
                  aria-label={formatMessage({ id: 'header.search.open', defaultMessage: 'Open search' })}
                  className="mt-10 h-full cursor-pointer border-primary-regular hover:border-b-2 focus:outline-none focus-visible:border-b-2"
                  onClick={onSearchClicked}
                  data-testid="open-search"
                >
                  <SearchIcon className={`size-24 stroke-0 2xl:size-26 ${'fill-primary-regular'}`} />
                </button>
                <Drawer
                  isOpen={isDrawerOpen}
                  direction="under"
                  className={searchDrawerClassName}
                  onClose={() => setIsDrawerOpen(false)}
                >
                  <Search
                    categories={categories}
                    autoFocus={isDrawerOpen}
                    submitButtonClassName={searchSubmitButtonClassName}
                    searchIconFocusedClassName={searchIconFocusedClassName}
                  />
                </Drawer>
              </li>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default HeaderNavigationDesktop;
